import { useMemo } from 'react';

import {
	classifyWithMatrixFilter,
	classifyWithSimpleFilter,
} from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/matrixClassification';

/**
 * Special (non-user) classes the classifiers can return, matching the
 * model-based flow's fixed styling for out-of-range / unmatched cells.
 */
const SPECIAL_CLASSES = {
	below_minimum: { id: 'below_minimum', label: 'Below Minimum', color: '#808080', opacity: 0.5 },
	unclassified: { id: 'unclassified', label: 'Unclassified', color: '#646464', opacity: 0.3 },
};

/**
 * Classifies collection hexagon cells — or DSP plots, which
 * `collectionDspToPlots` shapes identically — with a matrix/simple filter. The
 * pure, collection-side counterpart to the model's `useMatrixClassification`.
 *
 * Differences from the model hook (intentional):
 *   - operates on our flat `cell.stats` object (the classifiers read it via
 *     getPropertyValue), not OpenLayers feature metadata;
 *   - has no side effects — it does NOT touch the map. The layer reads
 *     `classByCellId` to color cells declaratively (see CollectionHexagonLayer),
 *     which keeps the layer WebGL-safe.
 *
 * @param {import('../utils/collectionHexagonsToCells').HexagonCell[]} cells
 * @param {object|null} filter - matrix/simple filter config, or null (no classification)
 * @param {object} [options]
 * @param {'area'|'count'} [options.percentageBasis='area'] - what each class's
 *   share is measured against. `'area'` (hexagons) divides by the total cell
 *   area, which is meaningful because cells tile the ground without overlapping.
 *   `'count'` (DSP plots) divides by the number of plots: image footprints
 *   overlap, so their summed area double-counts ground and a share of it would
 *   be a share of an inflated total. A per-plot share is also the statistic that
 *   matters for sample plots, and matches the model DSP path.
 * @returns {{
 *   classByCellId: Record<string|number, string>,
 *   results: Record<string, { id, label, color, opacity, count, area, objectArea, sharePercentage }>
 * }}
 *
 * `area` and `objectArea` are parallel accumulators, not alternatives: `area` is
 * the summed container area (hexagon cell size, or image footprint for DSP
 * plots) and stays the basis of the results table's area/footprint column and of
 * `sharePercentage`, while `objectArea` is the summed detected object area
 * (`stats.sum_object_area`) that every cost figure is priced on. Cells whose
 * detections carry no `sum_object_area` contribute 0, so `objectArea` is always
 * finite.
 *
 * `sharePercentage` is deliberately not named `areaPercentage`: which quantity
 * it is a share of depends on `percentageBasis`, so an area-specific name would
 * be wrong for DSP plots (where it is a share of plots, not of ground). The
 * model path's `areaPercentage` is count-based too — see
 * modifyLayers/dspColorFilter/useDspClassification.js — which is exactly the
 * confusion this name avoids.
 */
const useHexagonClassification = (cells, filter, { percentageBasis = 'area' } = {}) =>
	useMemo(() => {
		if (!filter || !cells?.length) return { classByCellId: {}, results: {} };

		const classify =
			filter.type === 'simple' ? classifyWithSimpleFilter : classifyWithMatrixFilter;

		// Seed per-class buckets (user classes + the two special categories) so
		// every class appears in the summary even with zero cells.
		const results = {};
		for (const cls of filter.classes ?? []) {
			results[cls.id] = {
				id: cls.id,
				label: cls.label,
				color: cls.color,
				opacity: cls.opacity ?? 0.7,
				count: 0,
				area: 0,
				objectArea: 0,
				sharePercentage: 0,
			};
		}
		for (const special of Object.values(SPECIAL_CLASSES)) {
			results[special.id] = {
				...special,
				count: 0,
				area: 0,
				objectArea: 0,
				sharePercentage: 0,
			};
		}

		const classByCellId = {};
		for (const cell of cells) {
			// A null result (invalid/incomplete filter) or an unknown id falls back
			// to "unclassified", matching the model's behavior.
			const raw = classify(cell.stats, filter) ?? 'unclassified';
			const classId = results[raw] ? raw : 'unclassified';
			classByCellId[cell.id] = classId;

			const bucket = results[classId];
			bucket.count += 1;
			bucket.area += cell.stats?.area ?? 0;
			// Parallel to `area`, not a replacement for it: the detected object
			// area the cost figures price on. `?? 0` is load-bearing — cells whose
			// detections carry no `sum_object_area` (and any DSP plot without one)
			// must contribute 0 rather than turn the running total into NaN.
			bucket.objectArea += cell.stats?.sum_object_area ?? 0;
		}

		// Area basis: derived from area (not count/cells.length) so it stays
		// consistent with the `area` shown beside it if per-cell area ever stops
		// being uniform. Count basis: a plain share of the classified units.
		// The share stays measured against container `area` on both bases — it
		// backs the results table's "% of area" / "% of plots" columns — while the
		// cost figures read `objectArea`. Re-basing this on object area would
		// silently turn "% of area" into a share of detected area.
		const total =
			percentageBasis === 'count'
				? cells.length
				: Object.values(results).reduce((sum, bucket) => sum + bucket.area, 0);
		for (const bucket of Object.values(results)) {
			const part = percentageBasis === 'count' ? bucket.count : bucket.area;
			bucket.sharePercentage = total > 0 ? Number(((part / total) * 100).toFixed(1)) : 0;
		}

		return { classByCellId, results };
	}, [cells, filter, percentageBasis]);

export default useHexagonClassification;
